import { emojiOf, SYMBOL_NAME } from "../icons";
import { readCron, shortestGap } from "./cron";
import { COLLECTION_NAME } from "./records";
import type { CollectionRules, Scope, WriteWord } from "./rules";

/**
 * A view's manifest (docs/views.md#3-manifest): JSON in a `<script type="application/coast+json">` in the document's head.
 * Only `name` is required; every other field is there to change a default, and this module fills the defaults in.
 */
/**
 * How a section opens deeper (docs/views.md#5-navigation-sections-and-links): as a screen of its own pushed over the one
 * under it, as a sheet over it, or inline, on the page itself. A host that can't present a screen or a sheet, such as a
 * view beside its list on a wide screen, opens it inline.
 */
export const PRESENTATIONS = ["screen", "sheet", "inline"] as const;
export type Presentation = (typeof PRESENTATIONS)[number];
export interface Section {
  params: string[];
  shows?: string;
  /** How the section opens unless `coast.nav.open` says: a screen of its own by default. */
  as?: Presentation;
}
/** A parameter a query or an action takes: its JSON type; `strings` is a list of up to `PARAM_LIMITS.items` strings. */
export type ParamType = "string" | "number" | "boolean" | "strings";
/**
 * How much a call's parameter holds: a value's characters, and a `strings` list's items, each of those characters; a
 * command's `text`, sent in place of a message, holds as much as a message (`LIMITS.textChars`).
 */
export const PARAM_LIMITS = { chars: 200, items: 10, commandChars: 4000 } as const;
/**
 * A query (docs/views.md#14-queries-and-actions). One that names a `connector` passes a GET to it through: `get` is a path under the
 * connector's base, where `{name}` takes a parameter, and the other parameters go in its query string. One that names a
 * `collection` reads the view's records as whoever asks can read them: its `where` is the page API's, where `"me"` is the
 * viewer and a value `"{name}"` takes a parameter, with a `sort` and a `limit`. One with `code` is the function of its
 * name in the view's server code, which the server runs in its sandbox. A device keeps a connector query's last answer to
 * show while it reads again, unless `cache` is false. In the manifest a parameter is its type, which the caller sets;
 * `{"value"}`, which the query always sends and the caller cannot change; or `{"type", "default"}`, sent when the caller
 * sets none.
 */
export type ParamValue = string | number | boolean;
export interface Params {
  params: Record<string, ParamType>;
  fixed?: Record<string, ParamValue>;
  defaults?: Record<string, ParamValue>;
}
export type QuerySpec =
  | ({ description?: string; cache?: false; connector: string; get: string } & Params)
  | ({
      description?: string;
      cache?: false;
      collection: string;
      where?: Record<string, unknown>;
      sort?: string[];
      limit?: number;
    } & Params)
  | ({
      description?: string;
      cache?: false;
      code: true;
      connectors: string[];
      /** The person's own connections it reads as whoever asks, such as `github` (docs/agent.md#4-connectors). */
      connections: string[];
    } & Params);
/**
 * An action (docs/views.md#14-queries-and-actions): the function of its name in the view's server code, which the server
 * runs in its sandbox as whoever calls it, with its declared parameters, and with the organization's connectors it
 * names, of those attached to the view.
 */
export interface ActionSpec extends Params {
  description?: string;
  connectors: string[];
}
/**
 * A trigger (docs/views.md#14-queries-and-actions): when one of the view's actions runs as the view itself, on a schedule
 * (five cron fields in UTC, at most every 15 minutes), after a change to one of its collections by anyone but the view,
 * or on an event an integration of one of its chats sends (docs/chats.md#6-posting-from-outside).
 */
export type TriggerSpec =
  | { action: string; schedule: string }
  | { action: string; change: string; on: ChangeKind[] }
  | { action: string; webhook: true };
export type ChangeKind = "insert" | "update" | "remove";
/**
 * A command (docs/views.md#14-queries-and-actions): `/<name> <text>` in the composer of a chat with the view runs one of
 * its actions as whoever sends it, with what follows the command as the action's `text`, in place of a message.
 */
export interface CommandSpec {
  action: string;
  description: string;
}
/** A command's name, as typed after `/`. */
export const COMMAND_NAME = /^[a-z][a-z0-9-]{0,23}$/;
/** The most commands one view brings. */
export const COMMAND_LIMIT = 10;
/** The shortest a schedule may run between firings, in minutes. */
export const SCHEDULE_MINUTES = 15;
export type CollectionQuerySpec = Extract<QuerySpec, { collection: string }>;
export interface CollectionSpec extends CollectionRules {
  guide?: string;
}
/** A view's icon as its manifest gives it: an emoji, an SF Symbol, or an SVG, which the server draws into a picture. */
export type ManifestIcon = { emoji: string } | { symbol: string } | { svg: string };
/** The most of an SVG a manifest's icon may hold. */
export const ICON_SVG_CHARS = 64_000;

/** A manifest's `icon`: one emoji, an SF Symbol's name, or an SVG's markup; undefined when it is none of them. */
export function manifestIcon(value: unknown): ManifestIcon | undefined {
  if (typeof value !== "string") return undefined;
  const emoji = emojiOf(value);
  if (emoji) return { emoji };
  if (SYMBOL_NAME.test(value)) return { symbol: value };
  const svg = value.trim();
  return /^<svg[\s/>]/i.test(svg) && svg.length <= ICON_SVG_CHARS ? { svg } : undefined;
}

export interface Manifest {
  name: string;
  description?: string;
  /**
   * What its pills, rows, and cards show for it (docs/views.md#3-manifest), unless a chat gives it its own; its
   * template's symbol when absent.
   */
  icon?: ManifestIcon;
  /** `half` is a posted view's half-height sheet (docs/views.md#15-views-in-messages); a pill never shows it. */
  layouts: ("full" | "desktop" | "half")[];
  sections: Record<string, Section>;
  collections: Record<string, CollectionSpec>;
  queries: Record<string, QuerySpec>;
  actions: Record<string, ActionSpec>;
  triggers: Record<string, TriggerSpec>;
  commands: Record<string, CommandSpec>;
  uses: string[];
  network: string[];
  runners: unknown[];
  preview?: { layout?: string; section?: string };
  chrome: "fade" | "clear" | "band";
  appearance: "auto" | "dark" | "light";
  font: "coast" | "own";
  /** `coast`, the default: the runtime gives the view the stock style and kit before its own styles; `own` leaves them out. */
  style: "coast" | "own";
}

export const MANIFEST = /<script type="application\/coast\+json">([\s\S]*?)<\/script>/;
const SECTION_NAME = /^[a-z][a-z0-9_-]{0,39}$/;
const QUERY_NAME = /^[a-z][a-zA-Z0-9_]{0,39}$/;
const PARAM_NAME = /^[a-z][a-zA-Z0-9_]{0,39}$/;
const WORDS: WriteWord[] = ["member", "admin", "author", "action", "integration", "runner"];
const SORT_KEY = /^-?[A-Za-z_][A-Za-z0-9_]{0,63}$/;
/** A where value that takes a parameter: the whole string `{name}`. */
export const PLACEHOLDER = /^\{([a-z][a-zA-Z0-9_]{0,39})\}$/;
/** The parameters a where clause takes, from its values and the values inside its conditions. */
export function placeholders(where: Record<string, unknown> | undefined): string[] {
  const found: string[] = [];
  const visit = (value: unknown) => {
    if (typeof value === "string") {
      const match = PLACEHOLDER.exec(value);
      if (match) found.push(match[1]!);
    } else if (Array.isArray(value)) value.forEach(visit);
    else if (value && typeof value === "object") Object.values(value).forEach(visit);
  };
  for (const value of Object.values(where ?? {})) visit(value);
  return found;
}
const SCOPES: Scope[] = ["chat", "view", "personal"];

/**
 * A query's or an action's parameters from the manifest: each one a type the caller sets, `{"value"}` that the call
 * always sends, or `{"type", "default"}` sent when the caller sets none. `label` names whose they are in an error.
 */
function readParams(
  raw: unknown,
  label: string,
): { params: Record<string, ParamType>; fixed: Record<string, ParamValue>; defaults: Record<string, ParamValue> } {
  const params: Record<string, ParamType> = {},
    fixed: Record<string, ParamValue> = {},
    defaults: Record<string, ParamValue> = {};
  const isType = (value: unknown): value is ParamType =>
    value === "string" || value === "number" || value === "boolean" || value === "strings";
  const isValue = (value: unknown): value is ParamValue =>
    (typeof value === "string" && value.length <= 200) ||
    typeof value === "boolean" ||
    (typeof value === "number" && Number.isFinite(value));
  if (raw === undefined) return { params, fixed, defaults };
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) throw new Error(`${label}'s params are an object.`);
  for (const [param, entry] of Object.entries(raw as Record<string, unknown>)) {
    if (!PARAM_NAME.test(param)) throw new Error(`${label} has an invalid param name: ${param}.`);
    if (isType(entry)) {
      params[param] = entry;
      continue;
    }
    const object =
      entry && typeof entry === "object" && !Array.isArray(entry) ? (entry as Record<string, unknown>) : null;
    if (object) only(object, "value" in object ? ["value"] : ["type", "default"], `${label}'s param ${param}`);
    if (object && "value" in object && isValue(object.value)) {
      fixed[param] = object.value;
      continue;
    }
    if (
      object &&
      isType(object.type) &&
      object.type !== "strings" &&
      isValue(object.default) &&
      typeof object.default === object.type
    ) {
      params[param] = object.type;
      defaults[param] = object.default;
      continue;
    }
    throw new Error(
      `${label}'s param ${param} is "string", "number", "boolean", or "strings"; {"value": …}, which it always sends; or {"type", "default"} of one value.`,
    );
  }
  return { params, fixed, defaults };
}

/** A collection's rules: its manifest entry with the defaults filled in, or the defaults for one it doesn't name. */
export function collectionSpec(manifest: Pick<Manifest, "collections">, name: string): CollectionSpec {
  return manifest.collections[name] ?? { scope: "chat", write: ["member"] };
}

/** The manifest in a view's HTML, checked and filled in; a view with none gets its name from the title. */
export function readManifest(html: string): Manifest {
  const block = MANIFEST.exec(html)?.[1];
  let raw: Record<string, unknown> = {};
  if (block) {
    try {
      raw = JSON.parse(block);
    } catch {
      throw new Error("The manifest is not valid JSON.");
    }
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) throw new Error("The manifest must be a JSON object.");
  }
  const title = /<title>([^<]{1,80})<\/title>/.exec(html)?.[1]?.trim();
  return normalize(raw, title);
}

function oneOf<T extends string>(value: unknown, options: readonly T[], fallback: T, field: string): T {
  if (value === undefined) return fallback;
  if (!options.includes(value as T)) throw new Error(`The manifest's ${field} is one of ${options.join(", ")}.`);
  return value as T;
}
/** The keys a manifest may give (docs/spec/view-format.md#21-the-whole, M3); `$schema` names a schema for an editor. */
const MANIFEST_KEYS = [
  "$schema",
  "name",
  "description",
  "icon",
  "layouts",
  "chrome",
  "appearance",
  "font",
  "style",
  "preview",
  "sections",
  "collections",
  "uses",
  "queries",
  "actions",
  "triggers",
  "commands",
  "network",
  "runners",
] as const;
const QUERY_KEYS = ["description", "cache", "params"] as const;
/** Refuses a key the format doesn't name (M3), saying which keys `owner` may have, so a misspelt one is plain. */
function only(value: Record<string, unknown>, keys: readonly string[], owner: string) {
  const other = Object.keys(value).find((key) => !keys.includes(key));
  if (other !== undefined) throw new Error(`${owner} has no key "${other}"; its keys are ${keys.join(", ")}.`);
}
/** An object a manifest gives, or a refusal naming `field` for anything else (docs/spec/view-format.md, M2). */
function object(value: unknown, field: string): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new Error(`The manifest's ${field} is an object.`);
  return value as Record<string, unknown>;
}
/** A string a manifest may give, cut to `most` characters; undefined when it gives none, refused when it isn't one. */
function text(value: unknown, field: string, most: number): string | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== "string") throw new Error(`The manifest's ${field} is a string.`);
  return value.slice(0, most);
}
function strings(value: unknown, field: string, test: (item: string) => boolean = () => true): string[] {
  if (value === undefined) return [];
  if (!Array.isArray(value) || value.some((item) => typeof item !== "string" || !test(item)))
    throw new Error(`The manifest's ${field} is a list of names.`);
  return value as string[];
}

export function normalize(raw: Record<string, unknown>, fallbackName?: string): Manifest {
  only(raw, MANIFEST_KEYS, "The manifest");
  if (raw.$schema !== undefined && typeof raw.$schema !== "string")
    throw new Error("The manifest's $schema is a string.");
  if (raw.name !== undefined && (typeof raw.name !== "string" || !raw.name.trim()))
    throw new Error("The manifest's name is a string with something in it.");
  const name = typeof raw.name === "string" ? raw.name.trim().slice(0, 60) : fallbackName;
  if (!name) throw new Error("A view's manifest needs a name.");
  const description = text(raw.description, "description", 400);
  const icon = raw.icon === undefined ? undefined : manifestIcon(raw.icon);
  if (raw.icon !== undefined && !icon)
    throw new Error(
      `A view's icon is one emoji, an SF Symbol's name such as "map", or an SVG's markup of at most ${ICON_SVG_CHARS / 1000}k characters.`,
    );
  const layouts = strings(
    raw.layouts,
    "layouts",
    (layout) => layout === "full" || layout === "desktop" || layout === "half",
  ) as Manifest["layouts"];
  const sections: Record<string, Section> = {};
  if (raw.sections !== undefined) {
    if (!raw.sections || typeof raw.sections !== "object" || Array.isArray(raw.sections))
      throw new Error("The manifest's sections are an object.");
    for (const [key, value] of Object.entries(raw.sections as Record<string, unknown>)) {
      if (!SECTION_NAME.test(key)) throw new Error(`Invalid section name: ${key}.`);
      const spec = object(value, `section ${key}`);
      only(spec, ["params", "shows", "as"], `Section ${key}`);
      const shows = spec.shows;
      if (shows !== undefined && (typeof shows !== "string" || !COLLECTION_NAME.test(shows)))
        throw new Error(`Section ${key} shows an invalid collection.`);
      const as = PRESENTATIONS.find((presentation) => presentation === spec.as);
      if (spec.as !== undefined && !as) throw new Error(`Section ${key} opens as one of ${PRESENTATIONS.join(", ")}.`);
      sections[key] = {
        params: strings(spec.params, `${key} params`, (param) => /^[a-z][a-zA-Z0-9_]{0,39}$/.test(param)),
        ...(shows ? { shows } : {}),
        ...(as ? { as } : {}),
      };
    }
  }
  const collections: Record<string, CollectionSpec> = {};
  if (raw.collections !== undefined) {
    if (!raw.collections || typeof raw.collections !== "object" || Array.isArray(raw.collections))
      throw new Error("The manifest's collections are an object.");
    for (const [key, value] of Object.entries(raw.collections as Record<string, unknown>)) {
      if (!COLLECTION_NAME.test(key)) throw new Error(`Invalid collection name: ${key}.`);
      const spec = object(value, `collection ${key}`);
      only(spec, ["scope", "write", "key", "guide"], `Collection ${key}`);
      const guide = text(spec.guide, `collection ${key}'s guide`, 600);
      const scope = oneOf(spec.scope, SCOPES, "chat", `${key} scope`);
      const write = (
        typeof spec.write === "string" ? spec.write.split(/\s+/) : spec.write === undefined ? ["member"] : spec.write
      ) as unknown;
      if (!Array.isArray(write) || !write.length || write.some((word) => !WORDS.includes(word as WriteWord)))
        throw new Error(`Collection ${key}'s write rule is made of ${WORDS.join(", ")}.`);
      if (spec.key !== undefined && spec.key !== "person")
        throw new Error(`Collection ${key}'s key can only be "person".`);
      collections[key] = {
        scope,
        write: write as WriteWord[],
        ...(spec.key ? { key: "person" as const } : {}),
        ...(guide !== undefined ? { guide } : {}),
      };
    }
  }
  const queries: Record<string, QuerySpec> = {};
  if (raw.queries !== undefined) {
    if (!raw.queries || typeof raw.queries !== "object" || Array.isArray(raw.queries))
      throw new Error("The manifest's queries are an object.");
    for (const [key, value] of Object.entries(raw.queries as Record<string, unknown>)) {
      if (!QUERY_NAME.test(key)) throw new Error(`Invalid query name: ${key}.`);
      const spec = object(value, `query ${key}`);
      if (spec.cache !== undefined && typeof spec.cache !== "boolean")
        throw new Error(`Query ${key}'s cache is true or false.`);
      const about = text(spec.description, `query ${key}'s description`, 300);
      const { params, fixed, defaults } = readParams(spec.params, `Query ${key}`);
      const known = (param: string) => param in params || param in fixed;
      const extra = {
        ...(Object.keys(fixed).length ? { fixed } : {}),
        ...(Object.keys(defaults).length ? { defaults } : {}),
      };
      const description = {
        ...(about !== undefined ? { description: about } : {}),
        ...(spec.cache === false ? { cache: false as const } : {}),
      };
      if (typeof spec.connector === "string") {
        only(spec, ["connector", "get", ...QUERY_KEYS], `Query ${key}`);
        if (!/^[a-z][a-z0-9-]{0,39}$/.test(spec.connector)) throw new Error(`Query ${key} names an invalid connector.`);
        if (typeof spec.get !== "string" || !/^\/[A-Za-z0-9/_.~{}-]{0,300}$/.test(spec.get))
          throw new Error(
            `Query ${key}'s get is a path under the connector's base, such as /users/{id}: letters, digits, and / _ . ~ - with {name} for a parameter, and no query string, since the other parameters go there.`,
          );
        for (const [, param] of spec.get.matchAll(/\{([^}]*)\}/g))
          if (!known(param!)) throw new Error(`Query ${key}'s path takes {${param}}, which its params lack.`);
        queries[key] = { ...description, connector: spec.connector, get: spec.get, params, ...extra };
      } else if (typeof spec.collection === "string" && COLLECTION_NAME.test(spec.collection)) {
        only(spec, ["collection", "where", "sort", "limit", ...QUERY_KEYS], `Query ${key}`);
        if (spec.where !== undefined && (!spec.where || typeof spec.where !== "object" || Array.isArray(spec.where)))
          throw new Error(`Query ${key}'s where is an object of fields and conditions.`);
        const where = spec.where as Record<string, unknown> | undefined;
        for (const param of placeholders(where))
          if (!known(param)) throw new Error(`Query ${key}'s where takes {${param}}, which its params lack.`);
        const sort = spec.sort === undefined ? [] : ([] as unknown[]).concat(spec.sort);
        if (sort.length > 4 || sort.some((field) => typeof field !== "string" || !SORT_KEY.test(field)))
          throw new Error(`Query ${key}'s sort is up to four field names, each with - first for descending.`);
        if (
          spec.limit !== undefined &&
          (!Number.isInteger(spec.limit) || (spec.limit as number) < 1 || (spec.limit as number) > 1000)
        )
          throw new Error(`Query ${key}'s limit is a whole number from 1 to 1000.`);
        queries[key] = {
          ...description,
          collection: spec.collection,
          ...(where ? { where } : {}),
          ...(sort.length ? { sort: sort as string[] } : {}),
          ...(spec.limit !== undefined ? { limit: spec.limit as number } : {}),
          params,
          ...extra,
        };
      } else if (spec.code === true) {
        only(spec, ["code", "connectors", "connections", ...QUERY_KEYS], `Query ${key}`);
        const connectors = strings(spec.connectors, `${key} connectors`, (name) => /^[a-z][a-z0-9-]{0,39}$/.test(name));
        const connections = strings(spec.connections, `${key} connections`, (name) =>
          /^[a-z][a-z0-9-]{0,39}$/.test(name),
        );
        queries[key] = { ...description, code: true, connectors, connections, params, ...extra };
      } else throw new Error(`Query ${key} names a connector or a collection, or says it is code.`);
    }
  }
  const actions: Record<string, ActionSpec> = {};
  if (raw.actions !== undefined) {
    if (!raw.actions || typeof raw.actions !== "object" || Array.isArray(raw.actions))
      throw new Error("The manifest's actions are an object.");
    for (const [key, value] of Object.entries(raw.actions as Record<string, unknown>)) {
      if (!QUERY_NAME.test(key)) throw new Error(`Invalid action name: ${key}.`);
      if (key in queries) throw new Error(`${key} names both a query and an action; each name is one tool.`);
      const spec = object(value, `action ${key}`);
      // An action writes and posts, so it never holds what a person's own connection read (docs/agent.md#4-connectors).
      if ("connections" in spec)
        throw new Error(`Only a code query reads a person's connections; action ${key} names connections.`);
      only(spec, ["description", "params", "connectors"], `Action ${key}`);
      const about = text(spec.description, `action ${key}'s description`, 300);
      const { params, fixed, defaults } = readParams(spec.params, `Action ${key}`);
      const connectors = strings(spec.connectors, `${key} connectors`, (name) => /^[a-z][a-z0-9-]{0,39}$/.test(name));
      actions[key] = {
        ...(about !== undefined ? { description: about } : {}),
        params,
        ...(Object.keys(fixed).length ? { fixed } : {}),
        ...(Object.keys(defaults).length ? { defaults } : {}),
        connectors,
      };
    }
  }
  const triggers: Record<string, TriggerSpec> = {};
  if (raw.triggers !== undefined) {
    if (!raw.triggers || typeof raw.triggers !== "object" || Array.isArray(raw.triggers))
      throw new Error("The manifest's triggers are an object.");
    for (const [key, value] of Object.entries(raw.triggers as Record<string, unknown>)) {
      if (!QUERY_NAME.test(key)) throw new Error(`Invalid trigger name: ${key}.`);
      const spec = object(value, `trigger ${key}`);
      if (typeof spec.action !== "string" || !(spec.action in actions))
        throw new Error(`Trigger ${key} names an action of the manifest's to run.`);
      if (typeof spec.schedule === "string") {
        only(spec, ["action", "schedule"], `Trigger ${key}`);
        const cron = readCron(spec.schedule);
        if ("error" in cron) throw new Error(`Trigger ${key}: ${cron.error}`);
        const gap = shortestGap(cron, Date.UTC(2026, 0, 1));
        if (gap !== null && gap < SCHEDULE_MINUTES)
          throw new Error(`Trigger ${key} runs at most every ${SCHEDULE_MINUTES} minutes.`);
        triggers[key] = { action: spec.action, schedule: spec.schedule.trim() };
      } else if (typeof spec.change === "string") {
        only(spec, ["action", "change", "on"], `Trigger ${key}`);
        if (!COLLECTION_NAME.test(spec.change)) throw new Error(`Trigger ${key} names an invalid collection.`);
        const on = strings(spec.on, `${key} on`, (kind) => ["insert", "update", "remove"].includes(kind));
        triggers[key] = {
          action: spec.action,
          change: spec.change,
          on: (on.length ? on : ["insert", "update", "remove"]) as ChangeKind[],
        };
      } else if (spec.webhook === true) {
        only(spec, ["action", "webhook"], `Trigger ${key}`);
        triggers[key] = { action: spec.action, webhook: true };
      } else throw new Error(`Trigger ${key} has a schedule, a change, or "webhook": true.`);
    }
  }
  const commands: Record<string, CommandSpec> = {};
  if (raw.commands !== undefined) {
    const given = object(raw.commands, "commands");
    if (Object.keys(given).length > COMMAND_LIMIT) throw new Error(`A view brings at most ${COMMAND_LIMIT} commands.`);
    for (const [key, value] of Object.entries(given)) {
      if (!COMMAND_NAME.test(key))
        throw new Error(`Invalid command name: ${key}; a lowercase letter, then up to 23 letters, digits, and -.`);
      const spec = object(value, `command ${key}`);
      only(spec, ["action", "description"], `Command ${key}`);
      const action = typeof spec.action === "string" ? actions[spec.action] : undefined;
      if (!action) throw new Error(`Command ${key} names an action of the manifest's to run.`);
      if (action.params.text !== "string")
        throw new Error(`Command ${key}'s action ${spec.action} takes the command's text as a string param, "text".`);
      const about = text(spec.description, `command ${key}'s description`, 80);
      if (!about) throw new Error(`Command ${key} says what it does in a description.`);
      commands[key] = { action: spec.action as string, description: about };
    }
  }
  const uses = strings(raw.uses, "uses", (use) => /^[a-z][a-z0-9_-]{0,39}\/[a-z][a-z0-9_-]{0,39}$/.test(use));
  const network =
    raw.network === undefined
      ? []
      : Array.isArray(raw.network) &&
          raw.network.every((origin) => typeof origin === "string" && /^https:\/\/[a-z0-9.-]+(:\d+)?$/i.test(origin))
        ? (raw.network as string[])
        : (() => {
            throw new Error("The manifest's network is a list of origins, such as https://api.example.com.");
          })();
  let preview: Manifest["preview"];
  if (raw.preview !== undefined) {
    const given = object(raw.preview, "preview");
    only(given, ["layout", "section"], "The manifest's preview");
    const layout = text(given.layout, "preview's layout", 40),
      section = text(given.section, "preview's section", 40);
    preview = { ...(layout !== undefined ? { layout } : {}), ...(section !== undefined ? { section } : {}) };
  }
  if (raw.runners !== undefined && !Array.isArray(raw.runners)) throw new Error("The manifest's runners are a list.");
  return {
    name,
    ...(description !== undefined ? { description } : {}),
    ...(icon ? { icon } : {}),
    layouts: layouts.length ? layouts : ["full", "desktop"],
    sections,
    collections,
    queries,
    actions,
    triggers,
    commands,
    uses,
    network,
    runners: (raw.runners as unknown[] | undefined) ?? [],
    ...(preview ? { preview } : {}),
    chrome: oneOf(raw.chrome, ["fade", "clear", "band"] as const, "fade", "chrome"),
    appearance: oneOf(raw.appearance, ["auto", "dark", "light"] as const, "auto", "appearance"),
    font: oneOf(raw.font, ["coast", "own"] as const, "coast", "font"),
    style: oneOf(raw.style, ["coast", "own"] as const, "coast", "style"),
  };
}

/** The section that shows a collection's records, so a link to a record finds its screen. */
export function sectionFor(manifest: Pick<Manifest, "sections">, collection: string): string | null {
  return Object.entries(manifest.sections).find(([, section]) => section.shows === collection)?.[0] ?? null;
}
